import type { StrokeDTO } from '@shared/protocol/messages';
import { GRID } from './strokes';

/** Selection maths for the move tool (grid units; pure, so it can be tested). */

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Bounds of the given strokes including half their brush width; null when there is nothing. */
export function boundsOf(strokes: readonly StrokeDTO[], idx: readonly number[]): Box | null {
  let b: Box | null = null;
  for (const i of idx) {
    const s = strokes[i];
    if (!s) continue;
    const r = s.s / 2;
    for (let k = 0; k < s.p.length; k += 2) {
      const x = s.p[k]!;
      const y = s.p[k + 1]!;
      if (!b) b = { x0: x - r, y0: y - r, x1: x + r, y1: y + r };
      else b = { x0: Math.min(b.x0, x - r), y0: Math.min(b.y0, y - r), x1: Math.max(b.x1, x + r), y1: Math.max(b.y1, y + r) };
    }
  }
  return b;
}

function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** The top-most visible (non-eraser) stroke under a point, or -1. `slack` is extra reach for fingers. */
export function hitStroke(strokes: readonly StrokeDTO[], x: number, y: number, slack = 4): number {
  for (let i = strokes.length - 1; i >= 0; i--) {
    const s = strokes[i]!;
    if (s.e) continue;
    const reach = s.s / 2 + slack;
    for (let k = 0; k + 3 < s.p.length; k += 2) {
      if (distToSegment(x, y, s.p[k]!, s.p[k + 1]!, s.p[k + 2]!, s.p[k + 3]!) <= reach) return i;
    }
  }
  return -1;
}

/** Strokes with at least half their points inside the box (erasers included, so erased holes travel along). */
export function strokesInBox(strokes: readonly StrokeDTO[], box: Box): number[] {
  const x0 = Math.min(box.x0, box.x1);
  const x1 = Math.max(box.x0, box.x1);
  const y0 = Math.min(box.y0, box.y1);
  const y1 = Math.max(box.y0, box.y1);
  const out: number[] = [];
  strokes.forEach((s, i) => {
    let inside = 0;
    for (let k = 0; k < s.p.length; k += 2) if (s.p[k]! >= x0 && s.p[k]! <= x1 && s.p[k + 1]! >= y0 && s.p[k + 1]! <= y1) inside++;
    if (inside * 2 >= s.p.length / 2) out.push(i);
  });
  return out;
}

/** Every stroke that shares a group (one stamp, fill or mirrored line) with a selected one. */
export function withGroups(idx: readonly number[], groups: readonly number[]): number[] {
  const g = new Set(idx.map((i) => groups[i]));
  return groups.flatMap((gi, i) => (g.has(gi) ? [i] : []));
}

/** Limits a move so every point stays on the page. */
export function clampMove(points: Box, dx: number, dy: number): [number, number] {
  return [Math.max(-points.x0, Math.min(GRID - points.x1, dx)), Math.max(-points.y0, Math.min(GRID - points.y1, dy))];
}

/** Point bounds (no brush width) — what the page limit applies to. */
export function pointBounds(strokes: readonly StrokeDTO[], idx: readonly number[]): Box | null {
  const b = boundsOf(
    idx.map((i) => ({ ...strokes[i]!, s: 0 })),
    idx.map((_, k) => k),
  );
  return b;
}

/** Copies of the strokes at idx shifted by (dx, dy); other strokes are kept as they are. */
export function moved(strokes: readonly StrokeDTO[], idx: readonly number[], dx: number, dy: number): StrokeDTO[] {
  const set = new Set(idx);
  return strokes.map((s, i) => (set.has(i) ? { ...s, p: s.p.map((v, k) => v + (k % 2 === 0 ? dx : dy)) } : s));
}

export type Arrange = 'front' | 'back' | 'up' | 'down';

/**
 * New stroke order (a permutation of indices) after moving the selected
 * pieces. A piece is a run of strokes sharing a group; "up"/"down" step over
 * one neighbouring piece, "front"/"back" go all the way. Selected pieces move
 * together and keep their own order.
 */
export function arrangeOrder(groups: readonly number[], selected: readonly number[], how: Arrange): number[] {
  const sel = new Set(selected);
  // Pieces: consecutive strokes of one group.
  const pieces: { idx: number[]; picked: boolean }[] = [];
  groups.forEach((g, i) => {
    const last = pieces[pieces.length - 1];
    if (last && groups[last.idx[0]!] === g) last.idx.push(i);
    else pieces.push({ idx: [i], picked: false });
  });
  for (const p of pieces) p.picked = p.idx.some((i) => sel.has(i));
  const block = pieces.filter((p) => p.picked);
  const rest = pieces.filter((p) => !p.picked);
  const same = groups.map((_, i) => i);
  if (block.length === 0) return same;
  let at: number;
  if (how === 'front') at = rest.length;
  else if (how === 'back') at = 0;
  else if (how === 'up') {
    const lastPicked = pieces.map((p) => p.picked).lastIndexOf(true);
    const next = pieces[lastPicked + 1];
    if (!next) return same;
    at = rest.indexOf(next) + 1;
  } else {
    const firstPicked = pieces.findIndex((p) => p.picked);
    const prev = pieces[firstPicked - 1];
    if (!prev) return same;
    at = rest.indexOf(prev);
  }
  return [...rest.slice(0, at), ...block, ...rest.slice(at)].flatMap((p) => p.idx);
}
